import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { BooleanConverter } from '../../inputConverter';
import { LayerService } from '../../services/layer.service';
import { PetlService } from '../../services/petl.service';
import { IMCMapView } from '../../types';

import { PetlBaseComponent } from './petl-base.component';
import { IProject } from './petl-types';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-petl-ng',
    styleUrls: ['../sharedStyles/base.scss', './petl-base.scss', './petl.component.scss'],
    templateUrl: './petl.component.html'
})

export class PetlComponent extends PetlBaseComponent implements OnInit {

    @Input()
    get zoomToLayer(): boolean {
        return this.service.zoomToLayer;
    }
    set zoomToLayer(value: boolean) {
        this.service.zoomToLayer = BooleanConverter(value);
    }

    private _enableMapFileDrop: boolean = true;
    @Input()
    get enableMapFileDrop(): boolean {
        return this._enableMapFileDrop;
    }
    set enableMapFileDrop(value: boolean) {
        this._enableMapFileDrop = BooleanConverter(value);
        if (this._enableMapFileDrop) {
            this.setDefaultDropElement();
        } else {
            this.removeMapDropEvents();
        }
    }

    @Input() dropElement: HTMLDivElement;
    @Input() mapView: IMCMapView;
    @Output() public readonly onfiledrop: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public readonly onpetlclose: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public readonly onpromote = new EventEmitter<IProject>();

    constructor(_vcr: ViewContainerRef, protected readonly layerService: LayerService, protected readonly petlService: PetlService, protected readonly cdr: ChangeDetectorRef) {
        super(_vcr, petlService, cdr);
        this.petlService.petlClose = this.onpetlclose;
        this.petlService.complete = this.onpromote;
        this.registerEvent('onfiledrop', this.onfiledrop);
        this.registerEvent('onpetlclose', this.onpetlclose);
        this.registerEvent('onpromote', this.onpromote);
    }

    async ngOnInit() {
        this.initialize();
        this.service.zoomToLayer = this.zoomToLayer;
        if (this.enableMapFileDrop) {
            await this.setDefaultDropElement();
        }
        this.mapView = await this.setupMapViewRef(this.mapView);
        this.petlService.setupLayerEvents(this.mapView);
    }

    get errorMessage() {
        return this.petlService.errorMessage;
    }

    get isColumnMappingButtonDisabled() {
        return this.isButtonDisabled('processor');
    }

    get isValidationButtonDisabled() {
        return this.isButtonDisabled('validate');
    }

    get isPromotedButtonDisabled() {
        return this.isButtonDisabled('promoted');
    }

    @Input()
    initialize = () => {
        this.petlService.initialize();
        this.detectChanges();
    }

    @Input()
    startOver = () => {
        this.petlService.startOver();
        this.detectChanges();
    }

    async setDefaultDropElement() {
        if (!this.dropElement) {
            const parent = await this.getClosestAncestor('mc-map');
            if (parent) {
                await parent.ready;
                this.dropElement = parent.mapView.container;
            }
        }
        this.setupMapDropEvents();
    }

    setupMapDropEvents() {
        this.dropElement.addEventListener('dragenter', this.ondragenter);
        this.dropElement.addEventListener('drop', this.ondrop);
        this.dropElement.addEventListener('mouseover', this.hideOverlay);
        this.dropElement.addEventListener('dragend', this.hideOverlay);
        this.dropElement.addEventListener('dragleave', this.hideOverlay);
        this.dropElement.addEventListener('click', this.hideOverlay);
        this.dropElement.addEventListener('dragover', this.ondragover);
    }

    removeMapDropEvents() {
        this.dropElement.removeEventListener('dragenter', this.ondragenter);
        this.dropElement.removeEventListener('drop', this.ondrop);
        this.dropElement.removeEventListener('mouseover', this.hideOverlay);
        this.dropElement.removeEventListener('dragend', this.hideOverlay);
        this.dropElement.removeEventListener('dragleave', this.hideOverlay);
        this.dropElement.removeEventListener('click', this.hideOverlay);
        this.dropElement.removeEventListener('dragover', this.ondragover);
    }

    ondragenter = (ev: DragEvent) => {
        const validItems = ['excel', 'spreadsheet', 'text'];
        if (ev.dataTransfer.items && ev.dataTransfer.items.length) {
            const item = ev.dataTransfer.items[0];
            if (validItems.filter((x) => item.type.indexOf(x) >= 0).length > 0) {
                ev.preventDefault();
                this.showMapDropOverlay(true);
            }
        }
    }

    ondragover = (ev: DragEvent) => {
        ev.preventDefault();
    }

    ondrop = (ev: DragEvent) => {
        ev.preventDefault();
        this.showMapDropOverlay(false);
        if (this.petlService.isValidFileDrop(ev)) {
            this.onfiledrop.emit(true);
            this.service.processFileDrop(ev);
        }
    }

    hideOverlay = () => {
        this.showMapDropOverlay(false);
    }

    isButtonDisabled(viewName: string) {
        return this.currentView !== viewName && !(this.petlService.nonNormalViews.indexOf(this.currentView) >= 0 && this.petlService.previousView === viewName);
    }

    closePetl() {
        this.showMapDropOverlay(false);
        this.onpetlclose.emit(true);
    }

    showMapDropOverlay(show: boolean = true) {
        const opacity = show ? '0.7' : '1';
        this.dropElement.style.setProperty('opacity', opacity);
    }

    clearError() {
        this.petlService.errorMessage = null;
    }

}
